// Task 192 D5 — bulk-classify modal for the Needs-Your-Review surface.
//
// Two-step flow:
//   1. Pick a client (cascade source).
//   2. Pick a project under the chosen client.
//   Submit writes classified_event.source = 'manual' for every event in
//   the selected (app, title) group(s).
//
// `single` and `bulk` modes are functionally identical from the modal's
// POV — they differ only in the input set's cardinality and the title
// copy. The parent `UnmatchedGroupsTable` decides which mode to open.
//
// Cascade pattern mirrors `web/frontend/src/pages/rules/AddRulePanel.tsx`
// (task 191) verbatim: the visible-clients filter strips archived rows
// but keeps the system-owned Internal client (tenants reclassify into
// Internal sub-projects like `_AI Tools` and `_Development` — that is
// the entire reason 189 broadened the predicate). The eligible-projects
// filter excludes archived rows and the literal terminal-fallback
// `Internal / Not Set` (slug-pair check, NOT `systemOwned` alone —
// `Personal / Not Set` is also `systemOwned` per task 131 and remains a
// valid manual-classify target).
//
// Submit path:
//   - For each group, GET /events/internal-group?app=…&title=… within
//     the active dateRange, collect event ids.
//   - De-duplicate ids across groups (defensive — the per-employee
//     groups are already disjoint by (app, title), but a future caller
//     could submit overlapping groups).
//   - POST /classified-events/bulk once with the deduplicated set.
//   - On success: invalidate the two fallback-samples query keys so the
//     classified rows fall out of the table; close; clear selection.
//   - On any failure: surface a single error string, keep the modal open.
//
// Mockup-mode: per task line 43 + non-goals, the submit handler is a
// no-op when `mockupMode === true`. The two-step picker still renders so
// designers can see the flow; it just doesn't issue network calls.

import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { SlidePanel } from '@/components/composites/SlidePanel';
import { Button } from '@/components/primitives/Button';
import { InfoBanner } from '@/components/primitives/InfoBanner';
import { useClients } from '@/hooks/useClients';
import { useProjects } from '@/hooks/useProjects';
import { useAppContext } from '@/context/AppContext';
import { useToken } from '@/lib/useToken';
import { apiFetch } from '@/lib/api';
import styles from './ClassifyGroupsModal.module.css';

export type ClassifyGroupsModalMode = 'single' | 'bulk';

export interface ClassifyGroup {
  app: string;
  /** Empty string represents the rows where event.title IS NULL — matches
   *  the way the upstream fallback-samples endpoint folds NULLs into the
   *  empty-string grouping key. */
  title: string;
}

interface ClassifyGroupsModalProps {
  open: boolean;
  mode: ClassifyGroupsModalMode;
  groups: readonly ClassifyGroup[];
  employeeId: string;
  onClose: () => void;
  /** Fired AFTER the modal closes on success. Parents use this to clear
   *  their selection set (the bulk-action bar's `selected` Map in D3). */
  onSuccess: () => void;
}

interface InternalGroupResponse {
  events: Array<{ id: string; occurredAt: string }>;
  total: number;
}

interface BulkClassifyResponse {
  classified: number;
}

export function ClassifyGroupsModal({
  open,
  mode,
  groups,
  employeeId,
  onClose,
  onSuccess,
}: ClassifyGroupsModalProps) {
  const { mockupMode, currentOrg, dateRange } = useAppContext();
  const getToken = useToken();
  const qc = useQueryClient();

  const { data: clients, isLoading: clientsLoading } = useClients();
  const { data: projects, isLoading: projectsLoading } = useProjects();

  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Visible clients — same filter shape as task 191's AddRulePanel:
  // hide archived, keep system-owned Internal.
  const visibleClients = useMemo(() => clients.filter((c) => !c.archivedAt), [clients]);

  // Eligible projects — same filter shape as task 191's AddRulePanel:
  // hide archived, hide the literal terminal-fallback Internal/Not Set
  // (slug-pair check, NOT `systemOwned` alone), require a selected client.
  const eligibleProjects = useMemo(() => {
    return projects.filter((p) => {
      if (p.archivedAt) return false;
      if (p.systemOwned && p.slug === 'not-set' && p.clientSlug === 'internal') return false;
      if (selectedClientId === '') return false;
      return p.clientId === selectedClientId;
    });
  }, [projects, selectedClientId]);

  const step: 'client' | 'project' = selectedClientId === '' ? 'client' : 'project';
  const canSubmit = selectedProjectId !== '' && !submitting;

  const reset = () => {
    setSelectedClientId('');
    setSelectedProjectId('');
    setSubmitting(false);
    setErrorMessage(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleBackToClient = () => {
    setSelectedClientId('');
    setSelectedProjectId('');
    setErrorMessage(null);
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    if (mockupMode) {
      // Mockup-mode: no-op submit per task line 43 + non-goals. The flow
      // is for designers; live writes only happen against a real tenant.
      reset();
      onSuccess();
      onClose();
      return;
    }

    setSubmitting(true);
    setErrorMessage(null);

    try {
      const token = await getToken();
      const orgId = currentOrg.id;

      // Fetch the underlying event-id set for each group (within the
      // active dateRange — DESIGN-RULES Rule 35: pages consume dateRange
      // from AppContext; the modal forwards it verbatim).
      const eventIdSet = new Set<string>();
      for (const group of groups) {
        const qs = new URLSearchParams();
        qs.set('app', group.app);
        qs.set('title', group.title);
        if (dateRange.start) qs.set('startDate', dateRange.start);
        if (dateRange.end) qs.set('endDate', dateRange.end);
        // The route caps at config.dashboard.maxLimit (5000 by default);
        // pass MAX_SAFE_INTEGER and let the route clamp — the same pattern
        // EmployeeReviewPage uses for the table-level fetch.
        qs.set('limit', String(Number.MAX_SAFE_INTEGER));
        const res = await apiFetch<InternalGroupResponse>(
          `/api/v1/orgs/${orgId}/employees/${employeeId}/events/internal-group?${qs.toString()}`,
          token,
        );
        for (const e of res.events) {
          eventIdSet.add(e.id);
        }
      }

      const eventIds = Array.from(eventIdSet);
      if (eventIds.length === 0) {
        // Defensive: a group with zero events shouldn't render here
        // (the table only shows groups with eventCount >= 1), but if
        // the data races against a worker reclassify pass, surface a
        // clean message instead of a 400 from the bulk endpoint.
        setErrorMessage('No events found for the selected group(s).');
        setSubmitting(false);
        return;
      }

      await apiFetch<BulkClassifyResponse>(
        `/api/v1/orgs/${orgId}/employees/${employeeId}/classified-events/bulk`,
        token,
        {
          method: 'POST',
          body: JSON.stringify({ eventIds, projectId: selectedProjectId }),
        },
      );

      // Invalidate the per-employee fallback-samples query keys so the
      // affected rows leave the table. Both the EmployeeReviewPage's
      // `employeeFallbackSamplesAll` key and any pages that key on
      // `employeeFallbackSamples` (the detail page's top-N summary)
      // refresh on next read.
      void qc.invalidateQueries({ queryKey: ['employeeFallbackSamplesAll', orgId, employeeId] });
      void qc.invalidateQueries({ queryKey: ['employeeFallbackSamples', orgId, employeeId] });
      // Also refresh the dashboard banner stats (task 188 wired the
      // counts off totalHours/totalSessions from the same endpoint).
      void qc.invalidateQueries({ queryKey: ['employeeDetail', orgId, employeeId] });

      reset();
      onSuccess();
      onClose();
    } catch (err) {
      setErrorMessage(
        err instanceof Error ? err.message : 'Could not classify — please try again.',
      );
      setSubmitting(false);
    }
  };

  const groupsLabel = mode === 'bulk' ? `${groups.length} groups` : '1 group';
  const submitLabel = submitting ? 'Classifying…' : `Classify ${groupsLabel}`;

  return (
    <SlidePanel
      open={open}
      onClose={handleClose}
      title="Classify"
      subtitle={
        mode === 'bulk'
          ? `Move ${groups.length} groups out of Internal into a real project.`
          : `Move "${groups[0]?.app}${groups[0]?.title ? ' — ' + groups[0].title : ''}" out of Internal.`
      }
      footer={
        <div className={styles.footer}>
          <Button variant="secondary" onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={!canSubmit}>
            {submitLabel}
          </Button>
        </div>
      }
    >
      <div className={styles.body}>
        {errorMessage && <InfoBanner tone="danger">{errorMessage}</InfoBanner>}

        <ol className={styles.steps} aria-label="Classify steps">
          <li className={step === 'client' ? styles.stepActive : styles.stepDone}>
            1. Choose a client
          </li>
          <li className={step === 'project' ? styles.stepActive : styles.stepPending}>
            2. Choose a project
          </li>
        </ol>

        {step === 'client' ? (
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Client</h3>
            {clientsLoading ? (
              <p className={styles.muted}>Loading clients…</p>
            ) : visibleClients.length === 0 ? (
              <p className={styles.muted}>No clients available.</p>
            ) : (
              <ul className={styles.optionList} role="list">
                {visibleClients.map((c) => (
                  <li key={c.id}>
                    <button
                      type="button"
                      className={styles.optionRow}
                      onClick={() => setSelectedClientId(c.id)}
                    >
                      <span className={styles.optionName}>{c.name}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : (
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <h3 className={styles.sectionTitle}>
                Project under{' '}
                <strong>
                  {visibleClients.find((c) => c.id === selectedClientId)?.name ?? 'client'}
                </strong>
              </h3>
              <button
                type="button"
                className={styles.backLink}
                onClick={handleBackToClient}
                disabled={submitting}
              >
                ← Back to client
              </button>
            </div>
            {projectsLoading ? (
              <p className={styles.muted}>Loading projects…</p>
            ) : eligibleProjects.length === 0 ? (
              <p className={styles.muted}>This client has no eligible projects.</p>
            ) : (
              <ul className={styles.optionList} role="radiogroup" aria-label="Project">
                {eligibleProjects.map((p) => (
                  <li key={p.id}>
                    <label
                      className={
                        selectedProjectId === p.id
                          ? `${styles.optionRow} ${styles.optionRowSelected}`
                          : styles.optionRow
                      }
                    >
                      <input
                        type="radio"
                        name="classify-project"
                        value={p.id}
                        checked={selectedProjectId === p.id}
                        onChange={() => setSelectedProjectId(p.id)}
                        className={styles.optionRadio}
                      />
                      <span className={styles.optionName}>{p.name}</span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </SlidePanel>
  );
}
